/**
 * SearchInputContainer.jsx
 * Created by michaelbray on 2/13/18.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Autosuggest from 'react-autosuggest';
import highlight from 'autosuggest-highlight';
import { debounce } from 'lodash';

import * as MapHelper from '../../helpers/mapHelper';
import * as Icons from '../../components/sharedComponents/icons/Icons';
import * as InventoryTag from '../../components/sharedComponents/inventoryTags/InventoryTag';

import { toTitleCase } from '../../helpers/stringHelper';
import * as searchActions from '../../actions/navigation/searchActions';

const theme = {
    container: "",
    containerOpen: "",
    input: "",
    inputOpen: "",
    inputFocused: "",
    suggestionsContainer: "search-suggestions-container",
    suggestionsContainerOpen: "open",
    suggestionsList: "search-suggestions-section-suggestions-container",
    suggestion: "search-suggestions-suggestion-wrapper",
    suggestionFirst: "first",
    suggestionHighlighted: "search-suggestion-focused",
    sectionContainer: "search-suggestions-section-container",
    sectionContainerFirst: "first",
    sectionTitle: "search-suggestions-section-title"
};

const propTypes = {
    placeholder: PropTypes.string,
    dispatch: PropTypes.func,
    updateSearchCriterias: PropTypes.func,
    submitSimpleSearch: PropTypes.func,
    fetchSuggestions: PropTypes.func,
    disableSearchOnSelect: PropTypes.bool,
    showAlways: PropTypes.bool,
    queryCriteriaPropertyName: PropTypes.string,
    onFocus: PropTypes.func,
    onRunSearch: PropTypes.func,
    onBlur: PropTypes.func,
    search: PropTypes.shape({
        showSearchInput: PropTypes.bool
    }),
    searchSuggestions: PropTypes.shape({
        suggestions: PropTypes.array,
        inventory_suggestions: PropTypes.array,
        content_suggestions: PropTypes.array,
        err: PropTypes.string
    }),
    searchInputID: PropTypes.string,
    ariaLabel: PropTypes.string
};

const defaultProps = {
    placeholder: "What are you looking for?",
    disableSearchOnSelect: false,
    showAlways: false,
    queryCriteriaPropertyName: "what",
    updateSearchCriterias: searchActions.updateSearchCriterias,
    submitSimpleSearch: searchActions.submitSimpleSearch,
    fetchSuggestions: searchActions.fetchSuggestions,
    searchSuggestions: PropTypes.shape({
        suggestions: [],
        inventory_suggestions: [],
        content_suggestions: []
    }),
    searchInputID: "",
    ariaLabel: 'Search'
};

// SearchInput is search input box and autosuggetions combo
export class SearchInputContainer extends React.Component {
    constructor(params) {
        super();

        this.state = {
            searchText: params.search[params.queryCriteriaPropertyName],
            showSuggestions: true
        };
        this.suggestionsFetchLock = false;
        this.highlightedSuggestion = null;
        this.selectedSuggestion = null;

        this.onChange = this.onChange.bind(this);
        this.onKeyPress = this.onKeyPress.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onSuggestionsFetchRequested = debounce(this.onSuggestionsFetchRequested.bind(this), 100, { leading: true, trailing: true });
        this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
        this.getSectionSuggestions = this.getSectionSuggestions.bind(this);
        this.getSuggestionValue = this.getSuggestionValue.bind(this);
        this.renderSectionTitle = this.renderSectionTitle.bind(this);
        this.renderSuggestion = this.renderSuggestion.bind(this);
        this.shouldRenderSuggestions = this.shouldRenderSuggestions.bind(this);
        this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
        this.onSuggestionHighlighted = this.onSuggestionHighlighted.bind(this);
        this.renderInputComponent = this.renderInputComponent.bind(this);
        this.renderSuggestionsContainer = this.renderSuggestionsContainer.bind(this);
    }

    componentDidUpdate(prevProps) {
        const { search, searchSuggestions } = this.props;

        // listen for changes in search input query string and update itself
        const nextWhat = search[this.props.queryCriteriaPropertyName];
        if (nextWhat
            && nextWhat !== ""
            && (!this.state.searchText || this.state.searchText === "" || nextWhat !== this.state.searchText)) {
            this.setState({ searchText: nextWhat });
        }

        // check and disable suggestions fetch lock if suggestion results from previous submission were returned
        if (this.suggestionsFetchLock && searchSuggestions) {
            if (searchSuggestions.err !== prevProps.searchSuggestions.err ||
                searchSuggestions.suggestions !== prevProps.searchSuggestions.suggestions ||
                searchSuggestions.inventory_suggestions !== prevProps.searchSuggestions.inventory_suggestions ||
                searchSuggestions.content_suggestions !== prevProps.searchSuggestions.content_suggestions) {
                this.suggestionsFetchLock = false;
            }
        }
    }

    // on suggestion value change (on keypress etc)
    onChange(event, { newValue }) {
        this.setState({ searchText: newValue });
        const updateParams = {};
        updateParams[this.props.queryCriteriaPropertyName] = newValue;
        this.props.dispatch(
            this.props.updateSearchCriterias(updateParams)
        );
    }

    // when auto suggests needs more data
    onSuggestionsFetchRequested({ value }) {
        if (!this.suggestionsFetchLock) {
            this.suggestionsFetchLock = true;
            this.props.dispatch(this.props.fetchSuggestions(value));
        }
    }

    // when suggestions clear
    onSuggestionsClearRequested() {
        // show something by default for empty?
    }

    // when auto suggestion was selected somehow using navs
    onSuggestionSelected(event, { suggestion }) {
        const suggestionText = this.getSuggestionValue(suggestion);
        this.highlightedSuggestion = null;
        this.selectedSuggestion = suggestion;

        this.setState({
            searchText: suggestionText
        }, () => {
            this.props.dispatch(
                this.props.updateSearchCriterias({
                    selectedSuggestion: suggestion
                })
            );

            setTimeout(() => {
                this.setState({
                    showSuggestions: false
                });
            }, 200);

            if (!this.props.disableSearchOnSelect) {
                // run search
                this.runSearch(suggestion);
            }
        });
    }

    // when auto suggestion was highlighted somehow using navs
    onSuggestionHighlighted({ suggestion }) {
        this.highlightedSuggestion = suggestion;
    }

    // listen for input key in the search/autosuggestion box
    onKeyPress(event) {
        if (event.key === "Enter") {
            // run search if Enter is not on a highlighted suggestion
            if (!this.highlightedSuggestion) {
                this.runSearch(null);
            }
            else if (this.state.showSuggestions) {
                // hide auto suggestions if auto suggestions are open, but don't run search yet
                this.highlightedSuggestion = null;
                this.setState({
                    showSuggestions: false
                });
            }
        }
        else if (this.selectedSuggestion != null || !this.state.showSuggestions) {
            // show auto suggestions on keypress
            this.selectedSuggestion = null;
            this.setState({
                showSuggestions: true
            });
            this.props.dispatch(
                this.props.updateSearchCriterias({
                    selectedSuggestion: null
                })
            );
        }
    }

    // on focus search input
    onFocus(event) {
        this.setState({
            showSuggestions: true
        });
        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    }

    // on removal of focus from search input
    onBlur(event) {
        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    }

    // construct target uri for direction navigation to landing page from search
    getTargetUri(item) {
        let targetUri;

        if (item.entity_type === "campground" && item.reservable) {
            targetUri = `/camping/campgrounds/${item.entity_id}`;
        }
        else if (item.entity_type === "permit") {
            targetUri = `/permits/${item.entity_id}`;
        }
        else if (item.entity_type === "vehiclepermit") {
            targetUri = `/vehiclepermits/${item.entity_id}`;
        }
        else if (item.entity_type === "tour") {
            targetUri = `/ticket/facility/tour/${item.entity_id}`;
        }
        else if (item.entity_type === "ticketfacility") {
            targetUri = `/ticket/facility/${item.entity_id}`;
        }
        else if (item.entity_type === "recarea") {
            targetUri = `/camping/gateways/${item.entity_id}`;
        }
        else {
            targetUri = `/camping/poi/${item.entity_id}`;
        }

        return targetUri;
    }

    // construct suggestion sections from fetched data
    getSuggestions() {
        const sections = [];

        if (this.props.searchSuggestions.inventory_suggestions && this.props.searchSuggestions.inventory_suggestions.length > 0) {
            sections[sections.length] = {
                title: "Recreation Areas, Facilities, Tours, Trails",
                suggestions: this.props.searchSuggestions.inventory_suggestions
            };
        }

        if (this.props.searchSuggestions.suggestions && this.props.searchSuggestions.suggestions.length > 0) {
            sections[sections.length] = {
                title: "Locations, City, State, Zip Code",
                suggestions: this.props.searchSuggestions.suggestions
            };
        }

        if (this.props.searchSuggestions.content_suggestions && this.props.searchSuggestions.content_suggestions.length > 0) {
            sections[sections.length] = {
                title: "Help Center, Help Topics, Frequently Asked Questions",
                suggestions: this.props.searchSuggestions.content_suggestions
            };
        }

        return sections;
    }

    // get search suggestion string to display in the drop down
    getSuggestionValue(suggestion) {
        let suggestionText = suggestion.text;
        if (suggestion.is_inventory) {
            // if suggestion is associated with an inventory name, then capitalize the name to clean it up
            suggestionText = toTitleCase(suggestionText);
        }

        return suggestionText;
    }

    // return suggestions for the given section
    getSectionSuggestions(section) {
        if (section && section.suggestions) {
            return section.suggestions;
        }
        return [];
    }

    getSuggestionIcon(suggestion) {
        if (suggestion.entity_type === "campground") {
            return (<InventoryTag.InventoryCamping isSpanItem isInverse />)
        }
        if (suggestion.entity_type === "ticketfacility" || suggestion.entity_type === "tour") {
            return (<InventoryTag.InventoryTicket isSpanItem isInverse />)
        }
        if (suggestion.entity_type === "permit" || suggestion.entity_type === "vehiclepermit") {
            return (<InventoryTag.InventoryPermit isSpanItem isInverse />)
        }
        if (suggestion.entity_type === "recarea") {
            return (<InventoryTag.InventoryRecArea isSpanItem isInverse />)
        }
        if (suggestion.entity_type === "kb") {
            return (
                <div className="help-suggestion-icon">
                    <Icons.IconHelpOutline isSpanItem />
                </div>
            )
        }
        if (suggestion.lat && !suggestion.entity_type) {
            return (
                <InventoryTag.InventoryMapPin isSpanItem isInverse />
            )
        }
        return (<InventoryTag.InventoryPOI isSpanItem isInverse />)
    }

    // trigger auto suggestion on second char
    shouldRenderSuggestions(value) {
        return this.state.showSuggestions && value.trim().length > 0;
    }

    // execute search request to navigate to selected item or navigate to search results
    runSearch(suggestion) {
        if (suggestion && suggestion.entity_type === "kb") {
            // navigate to ServiceNow knowledgebase
            const url = MapHelper.getTargetUri(suggestion);
            window.location.assign(url);
        }
        else if (suggestion && suggestion.reservable) {
            // navigate to reservable inventory
            const url = MapHelper.getTargetUri(suggestion);
            window.location.assign(url);
        }
        else if (this.props.onRunSearch) {
            // run search for specific inventory
            this.props.onRunSearch(this.state.searchText, suggestion);
        }
        else {
            // run search matching search text
            this.props.dispatch(
                this.props.submitSimpleSearch(this.state.searchText, null, suggestion)
            );
        }
    }

    // render suggestion section title
    renderSectionTitle(section) {
        if (section.title) {
            return (
                <span>{section.title}</span>
            );
        }

        return null;
    }

    // render suggestion and highlight match
    renderSuggestion(suggestion, { query }) {
        const suggestionText = this.getSuggestionValue(suggestion);

        let q = query;

        q = (q || "").trim();
        const matches = highlight.match(suggestionText, q);
        const parts = highlight.parse(suggestionText, matches);

        return (
            <div className="search-suggestion-container">
                <div className={"search-suggestion-icon"}>
                    {this.getSuggestionIcon(suggestion)}
                </div>
                <div className={"search-suggestion-content"}>
                    {
                        parts.map((part, index) => {
                            const className = part.highlight ? "highlight" : null;

                            return (
                                <span className={className} key={index}>{part.text}</span>
                            );
                        })
                    }
                    <div className="search-suggestion-subtitle">
                        {suggestion.parent_name &&
                            <span>{suggestion.parent_name}</span>
                        }
                        {suggestion.city && suggestion.state_code &&
                            <span> | Near {suggestion.city}, {suggestion.state_code}</span>
                        }
                    </div>
                </div>
            </div>
        );
    }

    renderSuggestionsContainer({ containerProps, children }) {
        let isAriaHidden = true;
        if (containerProps && containerProps.className && containerProps.className.includes('open')) {
            isAriaHidden = false;
        }

        return (
            <div {...containerProps} role={"list"} aria-hidden={isAriaHidden}>
                {children}
            </div>
        );
    }

    renderInputComponent(props) {
        const inputID = this.props.searchInputID !== "" ? this.props.searchInputID : 'header-search-input';
        return (
            <div>
                <label htmlFor={inputID} className="rec-sr-only">Search Recreation.gov</label>
                <input
                    aria-label="Enter text to search for campgrounds, tours, or other recreation activities, press arrow keys to select suggestions"
                    {...props} />
            </div>
        );
    }

    // render search input component
    render() {
        const inputProps = {
            id: this.props.searchInputID !== "" ? this.props.searchInputID : 'header-search-input',
            placeholder: this.props.placeholder,
            value: this.state.searchText ? this.state.searchText : '',
            onChange: this.onChange,
            onKeyPress: this.onKeyPress,
            onFocus: this.onFocus,
            onBlur: this.onBlur
        };

        const suggestions = this.getSuggestions();

        if (this.props.showAlways || this.props.search.showSearchInput) {
            return (
                <div className="form-item-wrap" role="search" aria-label={this.props.ariaLabel}>
                    <Autosuggest
                        id={inputProps.id}
                        theme={theme}
                        multiSection
                        suggestions={suggestions}
                        alwaysRenderSuggestions={false}
                        highlightFirstSuggestion={false}
                        onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                        getSectionSuggestions={this.getSectionSuggestions}
                        getSuggestionValue={this.getSuggestionValue}
                        renderInputComponent={this.renderInputComponent}
                        renderSuggestionsContainer={this.renderSuggestionsContainer}
                        renderSectionTitle={this.renderSectionTitle}
                        renderSuggestion={this.renderSuggestion}
                        shouldRenderSuggestions={this.shouldRenderSuggestions}
                        onSuggestionSelected={this.onSuggestionSelected}
                        onSuggestionHighlighted={this.onSuggestionHighlighted}
                        focusFirstSuggestion={false}
                        inputProps={inputProps} />
                    <div className="search-icon-holder" aria-hidden="true">
                        <Icons.IconSearch />
                    </div>
                </div>
            );
        }

        return null;
    }
}

SearchInputContainer.propTypes = propTypes;
SearchInputContainer.defaultProps = defaultProps;

function select(state) {
    return {
        search: state.search,
        searchSuggestions: state.searchSuggestions
    };
}

export default connect(select)(SearchInputContainer);
